import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Line } from '@react-three/drei';
import * as THREE from 'three';
import { useClickStore } from '../store/clickStore.js';
import { useHoleDisplayRadii } from '../store/holeDisplayStore.js';

export const HOLE_HIGHLIGHT_COLOR = '#22d3ee';

export function resolveSelectedHoleId(state, holes) {
  if (!state || state.clickType !== 'info' || !holes) return null;
  const details = state.objectDetails || {};
  const candidates = [
    details.holeKey,
    details.holeID,
    details.Hole_ID,
    details.holeid,
    details.HoleID,
    details.BHID,
    details.bhid,
    state.objectDetailsObject?.name,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const id = candidates[i];
    if (id != null && holes[id]) return id;
    if (id != null && holes[String(id)]) return String(id);
  }
  const hid = details.Hole_ID || details.holeID;
  const dataset = details.DataSet;
  if (dataset && hid) {
    const composite = `${dataset}::${hid}`;
    if (holes[composite]) return composite;
  }
  if (hid != null) {
    const matches = Object.keys(holes).filter((key) => {
      const row = holes[key];
      return row?.data?.Hole_ID === hid || row?.holeId === hid || String(row?.holeId) === String(hid);
    });
    if (matches.length === 1) return matches[0];
  }
  return null;
}

function pulseMaterial(material) {
  if (!material) return;
  const mats = Array.isArray(material) ? material : [material];
  const opacity = 0.4 + 0.35 * Math.sin(Date.now() * 0.004);
  for (let i = 0; i < mats.length; i += 1) {
    if (mats[i]) mats[i].opacity = opacity;
  }
}

/**
 * Parent-hole marker (cyan). Assay / lithology / block selection stays gold via HighlightedObject.
 */
export default function SelectedHoleHighlight({ holes, getLinePoints }) {
  const meshRef = useRef();
  const invalidate = useThree((s) => s.invalidate);
  const [selectedHoleName, setSelectedHoleName] = useState(null);
  const { highlight: highlightRadius, highlightLineWidth } = useHoleDisplayRadii();

  useEffect(() => {
    const unsub = useClickStore.subscribe((state) => {
      setSelectedHoleName(resolveSelectedHoleId(state, holes));
    });
    setSelectedHoleName(resolveSelectedHoleId(useClickStore.getState(), holes));
    return () => unsub();
  }, [holes]);

  useEffect(() => {
    if (selectedHoleName) invalidate();
  }, [selectedHoleName, invalidate]);

  useFrame(() => {
    if (!selectedHoleName) return;
    pulseMaterial(meshRef.current?.material);
    invalidate();
  });

  const linePoints = useMemo(() => {
    if (!selectedHoleName || !getLinePoints) return null;
    return getLinePoints(selectedHoleName);
  }, [selectedHoleName, getLinePoints]);

  if (!selectedHoleName || !holes[selectedHoleName]) return null;

  const skipPick = { mdvSkipPick: true };

  if (linePoints && linePoints.length >= 2) {
    return (
      <Line
        ref={meshRef}
        points={linePoints}
        closed={false}
        lineWidth={highlightLineWidth}
        color={HOLE_HIGHLIGHT_COLOR}
        transparent
        opacity={0.75}
        depthTest={false}
        name="HolesHighlight"
        userData={skipPick}
        raycast={() => false}
      />
    );
  }

  const holeData = holes[selectedHoleName];
  const pts = holeData.points;
  if (!pts || pts.length < 2) return null;

  const start = new THREE.Vector3(pts[0].x, pts[0].y, pts[0].z);
  const end = new THREE.Vector3(pts[1].x, pts[1].y, pts[1].z);
  const direction = new THREE.Vector3().subVectors(end, start);
  const length = Math.max(direction.length(), 0.05);
  const center = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
  const quaternion = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(0, 1, 0),
    direction.normalize()
  );

  return (
    <mesh
      ref={meshRef}
      position={center}
      quaternion={quaternion}
      scale={[1, length, 1]}
      name="HolesHighlight"
      userData={skipPick}
      raycast={() => false}
      frustumCulled={false}
    >
      <cylinderGeometry key={highlightRadius} args={[highlightRadius, highlightRadius, 1, 10]} />
      <meshBasicMaterial
        color={HOLE_HIGHLIGHT_COLOR}
        transparent
        opacity={0.75}
        depthTest={false}
        toneMapped={false}
      />
    </mesh>
  );
}
